import { useMemo } from 'react';
import type { ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import { getCompanyIdLabel } from '@/utils/companyId';
import type { AssistanceEncart } from '@/features/assistance/assistanceWording';

interface ContractHtmlPreviewProps {
  formData: ContractFormState;
  warrantyName?: string;
  warrantyDuration?: number;
  warrantyFinancialCap?: string;
  warrantyKmLimit?: string;
  warrantyType?: number;
  warrantyParts?: any[];
  currentStep?: number;
  guarantorName?: string;
  guarantorSiren?: string;
  guarantorAddress?: string;
  guarantorCpCity?: string;
  guarantorPhone?: string;
  guarantorEmail?: string;
  isIntermediary?: boolean;
  diagnostic?: string;
  paymentMethodText?: string;
  logoUrl?: string | null;
  /** Encart d'assistance déjà composé, ou null si le contrat n'en porte pas. */
  assistanceEncart?: AssistanceEncart | null;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR');
  } catch {
    return '—';
  }
}

function formatPrice(value?: number): string {
  if (value === undefined || value === null) return '—';
  return `${value.toFixed(2)} € TTC`;
}

function formatMonthlyPrice(sellingPrice: number, duration: number, monthlyPriceWithFees?: number): string {
  const monthly = monthlyPriceWithFees && monthlyPriceWithFees > 0
    ? monthlyPriceWithFees
    : sellingPrice / duration;
  return `${duration} x ${monthly.toFixed(2)} € TTC`;
}

function formatKm(value?: number): string {
  if (value === undefined || value === null) return '—';
  return `${value.toLocaleString('fr-FR')} km`;
}

const HIGHLIGHT_STYLE: React.CSSProperties = {
  outline: '2px solid #8074af',
  outlineOffset: -1,
  borderRadius: 3,
};

/**
 * HTML preview of contract PDF page 1 — rendered client-side.
 * Reproduces the structure of affinicar_conditions_v2.html template.
 * Scaled to fit inside a ~700px panel.
 */
export function ContractHtmlPreview({
  formData,
  warrantyName,
  warrantyDuration,
  warrantyFinancialCap,
  warrantyKmLimit,
  warrantyType = 0,
  warrantyParts,
  currentStep,
  guarantorName,
  guarantorSiren,
  guarantorAddress,
  guarantorCpCity,
  guarantorPhone,
  guarantorEmail,
  isIntermediary = false,
  diagnostic,
  paymentMethodText,
  logoUrl,
  assistanceEncart,
}: ContractHtmlPreviewProps) {
  // Labels conditionnels identiques au template affinicar_conditions_v2.html
  const label = warrantyType === 1 ? 'maintenance' : 'garantie';
  const Label = warrantyType === 1 ? 'Maintenance' : 'Garantie';
  const bannerLabel = warrantyType === 1 ? 'Prestation' : 'Garantie';
  const beneficiaryLabel = warrantyType === 1 ? 'Client' : 'Bénéficiaire';
  const vehicleLabel = warrantyType === 1 ? 'Véhicule' : 'Véhicule garanti';
  const guarantorLabel = isIntermediary
    ? 'Intermédiaire de vente'
    : warrantyType === 1 ? 'Prestataire' : 'Garant';
  const guarantorArticle = isIntermediary
    ? "l'Intermédiaire de vente"
    : warrantyType === 1 ? 'le Prestataire' : 'le Garant';
  const savUrl = warrantyType === 1 ? 'sav-contrat.affinicar.com' : 'sav-garantie.affinicar.com';

  const showDates = currentStep === undefined || currentStep >= 3;

  const endDate = useMemo(() => {
    if (!showDates || !formData.subscriptionDate || !warrantyDuration) return '—';
    try {
      const start = new Date(formData.subscriptionDate);
      start.setMonth(start.getMonth() + warrantyDuration);
      return start.toLocaleDateString('fr-FR');
    } catch {
      return '—';
    }
  }, [formData.subscriptionDate, warrantyDuration, showDates]);

  const vehicleSearchType = formData.vehicle.vehicleSearchType;

  const groupedParts = useMemo(() => {
    if (!warrantyParts || warrantyParts.length === 0) return null;
    const byCategory: Record<string, { label: string; sortOrder: number; parts: string[] }> = {};
    warrantyParts.forEach((part: any) => {
      const catKey = part.category?.catKey || 'uncategorized';
      const categoryLabel = part.category?.label || part.subcategory?.label || catKey;
      const categorySortOrder = part.category?.sortOrder || 999;
      const partLabel = part.partLabel || part.label || part.partKey;
      if (!byCategory[catKey]) {
        byCategory[catKey] = { label: categoryLabel, sortOrder: categorySortOrder, parts: [] };
      }
      byCategory[catKey].parts.push(partLabel);
    });
    return Object.entries(byCategory)
      .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
      .map(([, data]) => ({ label: data.label, parts: data.parts }));
  }, [warrantyParts]);

  const step1Highlight = currentStep === 1 ? HIGHLIGHT_STYLE : undefined;
  const step2Highlight = currentStep === 2 ? HIGHLIGHT_STYLE : undefined;
  const step3Highlight = currentStep === 3 ? HIGHLIGHT_STYLE : undefined;
  const step4Highlight = currentStep === 4 ? HIGHLIGHT_STYLE : undefined;

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Roboto', -apple-system, sans-serif",
        color: '#1a1a1a',
        fontSize: '6pt',
        lineHeight: 1.3,
        background: '#fff',
        width: '100%',
        aspectRatio: '1 / 1.414',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* HEADER */}
      <div style={{ padding: '8px 10px 4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ width: 120 }}>
          <img src={logoUrl || '/assets/images/affinicar/logo.png'} alt="Logo" style={{ maxWidth: 120, maxHeight: 28 }} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '11pt', fontWeight: 700 }}>Contrat de {label}</div>
          <div style={{ fontSize: '8pt', fontWeight: 600, color: '#404040' }}>Conditions Particulières</div>
        </div>
      </div>

      {/* CONTRACT BANNER */}
      <div style={{
        margin: '3px 10px',
        padding: '5px 10px',
        background: '#f5f5f5',
        border: '1px solid #d4d4d4',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 4,
        ...step3Highlight,
      }}>
        <BannerItem label={bannerLabel} value={warrantyName || '—'} />
        <Separator />
        <BannerItem label="Date de création" value={showDates ? formatDate(formData.subscriptionDate) : '—'} />
        <Separator />
        <BannerItem label="N° de contrat" value="—" />
      </div>

      {/* TEXTE INTRODUCTIF */}
      <div style={{ padding: '2px 10px 4px', fontSize: '6pt', lineHeight: 1.4, color: '#1a1a1a', textAlign: 'justify' }}>
        Le Contrat de {Label}, constitué des présentes Conditions Particulières, de la confirmation
        de la souscription et des Conditions Générales, est conclu entre le {beneficiaryLabel} et {guarantorArticle} et
        pour le Véhicule désigné ci-après :
      </div>

      {/* 3 COLUMNS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1.1fr 0.8fr 1.1fr',
        gap: 5,
        margin: '3px 10px',
      }}>
        {/* BENEFICIAIRE */}
        <ColumnCard title={beneficiaryLabel} highlight={step1Highlight}>
          {formData.beneficiary.society && (
            <InfoRow label="Société" value={formData.beneficiary.society} />
          )}
          <InfoRow
            label="Nom"
            value={[formData.beneficiary.firstName, formData.beneficiary.lastName].filter(Boolean).join(' ') || '—'}
          />
          <InfoRow label="Adresse" value={formData.beneficiary.address || '—'} />
          {formData.beneficiary.address2 && (
            <InfoRow label="" value={formData.beneficiary.address2} />
          )}
          <InfoRow label="" value={[formData.beneficiary.cp, formData.beneficiary.city].filter(Boolean).join(' ') || '—'} />
          <InfoRow label="Téléphone" value={formData.beneficiary.phoneNumber || '—'} />
          <InfoRow label="Email" value={formData.beneficiary.email || '—'} />
        </ColumnCard>

        {/* VEHICULE */}
        <ColumnCard title={vehicleLabel} highlight={step2Highlight}>
          <InfoRow label="Marque" value={formData.vehicle.brand || '—'} />
          <InfoRow label="Modèle" value={formData.vehicle.model || '—'} />
          <InfoRow label="Version" value={formData.vehicle.finishes || '—'} />
          {vehicleSearchType === 2 ? (
            <InfoRow label="VIN" value={formData.vehicle.serialNumber || '—'} />
          ) : (
            <InfoRow label="Immat." value={formData.vehicle.registration || '—'} />
          )}
          <InfoRow label="Mise en circ." value={formatDate(formData.vehicle.entryIntoService)} />
          <InfoRow label="Kilométrage" value={formatKm(formData.kmStart)} />
        </ColumnCard>

        {/* GARANT / PRESTATAIRE / INTERMÉDIAIRE */}
        <ColumnCard title={guarantorLabel}>
          <InfoRow label="Société" value={guarantorName || '—'} />
          <InfoRow label={getCompanyIdLabel(guarantorSiren)} value={guarantorSiren || '—'} />
          <InfoRow label="Adresse" value={guarantorAddress || '—'} />
          <InfoRow label="" value={guarantorCpCity || '—'} />
          {guarantorPhone && (
            <InfoRow label="Téléphone" value={guarantorPhone} />
          )}
          {guarantorEmail && (
            <InfoRow label="Email" value={guarantorEmail} />
          )}
        </ColumnCard>
      </div>

      {/* DURATION CARD */}
      <div style={{
        margin: '3px 10px',
        padding: '5px 10px',
        background: '#f5f5f5',
        border: '1px solid #d4d4d4',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 4,
        ...step3Highlight,
      }}>
        <DurationItem label="Durée" value={warrantyDuration ? `${warrantyDuration} mois` : '—'} />
        <Separator />
        <DurationItem label="Plafond financier" value={warrantyFinancialCap || '—'} />
        <Separator />
        <DurationItem label="Limite km" value={warrantyKmLimit || '—'} />
        <Separator />
        <DurationItem label="Date de début" value={showDates ? formatDate(formData.subscriptionDate) : '—'} />
        <Separator />
        <DurationItem label="Date de fin" value={showDates ? endDate : '—'} />
      </div>

      {/* PARTS COVERED */}
      <div style={{ margin: '3px 10px', border: '1px solid #d4d4d4', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', ...step3Highlight }}>
        <div style={{
          background: '#1a1a1a',
          color: '#fff',
          padding: '5px 10px',
          fontWeight: 700,
          fontSize: '8pt',
          textTransform: 'uppercase',
        }}>
          Organes et pièces couverts par le contrat
        </div>
        <div style={{ padding: '6px 10px', fontSize: '5.5pt', overflow: 'hidden', flex: 1 }}>
          {groupedParts ? (
            groupedParts.map((cat, i) => (
              <div key={i} style={{ marginBottom: 2 }}>
                <span style={{ fontWeight: 700, fontSize: '6pt' }}>{cat.label}</span>
                {' : '}
                <span style={{ color: '#404040' }}>{cat.parts.join(', ')}.</span>
              </div>
            ))
          ) : (
            <span style={{ color: '#737373', fontStyle: 'italic' }}>
              Détail disponible après sélection de la {label}
            </span>
          )}
        </div>
        {groupedParts && (
          <div style={{
            padding: '4px 10px',
            background: '#f5f5f5',
            fontSize: '5.5pt',
            fontStyle: 'italic',
            color: '#404040',
            borderTop: '1px solid #d4d4d4',
          }}>
            * Les petites fournitures et les lubrifiants restent à la charge du bénéficiaire.
            Veillez à entretenir le véhicule dans le respect des préconisations constructeur.
          </div>
        )}
      </div>

      {/* DIAGNOSTIC (si renseigné) */}
      {diagnostic && (
        <div style={{
          margin: '3px 10px',
          padding: '5px 6px',
          background: '#f5f5f5',
          border: '1px solid #d4d4d4',
          fontSize: '6pt',
          lineHeight: 1.3,
        }}>
          <div style={{ fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Informations complémentaires :</div>
          <div>{diagnostic}</div>
        </div>
      )}

      {/* ENCART ASSISTANCE — mêmes proportions que le PDF (4 cellules en ligne) */}
      {assistanceEncart && (
        <div style={{ margin: '3px 10px', border: '1px solid #d4d4d4', background: '#fff' }}>
          <div style={{
            background: '#1a1a1a',
            color: '#fff',
            padding: '4px 10px',
            fontWeight: 700,
            fontSize: '8pt',
            textTransform: 'uppercase',
          }}>
            {assistanceEncart.title}
          </div>
          <div style={{ padding: '5px 10px' }}>
            <div style={{ fontSize: '6pt', lineHeight: 1.3 }}>
              {assistanceEncart.intro}
            </div>
            {assistanceEncart.footer && (
              <div style={{
                marginTop: 4,
                paddingTop: 3,
                borderTop: '1px solid #d4d4d4',
                fontSize: '5.5pt',
                lineHeight: 1.3,
                color: '#404040',
              }}>
                {assistanceEncart.footer}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: diagnostic ? undefined : 0 }} />

      {/* BOTTOM: HELP + PRICE */}
      <div style={{
        margin: '3px 10px 4px',
        marginTop: 'auto',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 5,
      }}>
        {/* HELP CARD */}
        <div style={{
          background: '#404040',
          color: '#fff',
          padding: '8px 10px',
          border: '1px solid #404040',
        }}>
          <div style={{ fontSize: '9pt', fontWeight: 700, textTransform: 'uppercase', textAlign: 'center', marginBottom: 8 }}>
            En cas de panne ?
          </div>
          <div style={{ fontSize: '6.5pt', lineHeight: 1.4, marginBottom: 6 }}>
            La gestion de votre contrat est confiée au Prestataire AFFINICAR, spécialiste de la gestion
            de {label}. En cas de panne, votre déclaration et demande de {label} est à réaliser sur
            le site <strong>{savUrl}</strong> ou par mail à <strong>sav@affinicar.com</strong>.
            Vous pouvez aussi nous joindre au <strong>0 800 944 665</strong> (appel gratuit).
          </div>
          <div style={{
            fontSize: '6pt',
            fontStyle: 'italic',
            paddingTop: 6,
            borderTop: '1px solid #737373',
          }}>
            NB : Aucune réparation effectuée ne sera prise en charge sans accord préalable écrit.
          </div>
        </div>

        {/* PRICE + SIGNATURE */}
        <div style={{ border: '1px solid #d4d4d4', borderBottom: 'none', display: 'flex', flexDirection: 'column', ...step3Highlight, ...step4Highlight }}>
          <div style={{
            background: '#f5f5f5',
            padding: '6px 10px',
            textAlign: 'right',
            borderBottom: '1px solid #d4d4d4',
          }}>
            <div style={{ fontSize: '6.5pt', color: '#737373', textTransform: 'uppercase', fontWeight: 600, marginBottom: 3 }}>
              Prix de la {label}
            </div>
            <div style={{ fontSize: '12pt', fontWeight: 700, color: '#1a1a1a' }}>
              {formData.includedOption
                ? (formData.includedOptionLabel || 'Incluse')
                : formData.paymentType === 2 && warrantyDuration
                  ? formatMonthlyPrice(Number(formData.sellingPrice) || 0, warrantyDuration, Number(formData.monthlyPriceWithFees) || undefined)
                  : formatPrice(formData.sellingPrice)}
            </div>
          </div>
          <div style={{ padding: '6px 10px', flex: 1 }}>
            <div style={{ fontSize: '8pt', fontWeight: 700, textTransform: 'uppercase', color: '#404040', marginBottom: 6 }}>
              Signature du {beneficiaryLabel.toLowerCase()}
            </div>
            <div style={{ height: 40 }} />
          </div>
        </div>
      </div>

      {/* PAYMENT METHOD TEXT */}
      {paymentMethodText && (
        <div style={{ margin: '0 10px 4px', fontSize: '5.5pt', lineHeight: 1.3, color: '#404040', textAlign: 'justify' }}>
          {paymentMethodText}
        </div>
      )}
    </div>
  );
}

/* --- Sub-components --- */

function BannerItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: '6pt', color: '#737373', textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{ fontSize: '8pt', color: '#1a1a1a', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </div>
  );
}

function DurationItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: '6pt', color: '#737373', textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{ fontSize: '7pt', color: '#1a1a1a', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </div>
  );
}

function Separator() {
  return <div style={{ width: 1, height: 24, background: '#d4d4d4', flexShrink: 0 }} />;
}

function ColumnCard({ title, children, highlight }: { title: string; children: React.ReactNode; highlight?: React.CSSProperties }) {
  return (
    <div style={{ border: '1px solid #d4d4d4', ...highlight }}>
      <div style={{
        background: '#f5f5f5',
        padding: '4px 8px',
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '6.5pt',
        textTransform: 'uppercase',
        borderBottom: '1px solid #d4d4d4',
      }}>
        {title}
      </div>
      <div style={{ padding: '5px 8px' }}>{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', padding: '2px 0' }}>
      <span style={{ color: '#737373', fontWeight: 600, minWidth: 60, fontSize: '5.5pt' }}>
        {label}
      </span>
      <span style={{ flex: 1, fontSize: '6pt', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </span>
    </div>
  );
}
